import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card } from 'ui/card';
import { Button } from 'ui/button';
import { Input } from 'ui/input';
import { Label } from 'ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'ui/select';
import { Checkbox } from 'ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'ui/table';
import { BarChart3, CheckCircle, FileText, Filter, Download, Plus, Eye, MailCheck } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Currency, CurrencyHead, getAllCurrencies, getCurrencyHead } from 'services/currencyService';
import { approveReceipts, getMiqaatNiyazDashboardList, getMiqaatUsers, getReceiptPdf, getVenues, SendReceiptEmail } from 'services/mannatNiyazService';
import useDebounce from 'hooks/useDebounce';
import { FullScreenLoading } from 'components/ui/loader';
import { useAuth } from 'contexts/AuthContext';
import axios from 'axios';
// import Pagination from 'ui/pagination';
import { useScreenPermission } from "hooks/useScreenPermission";
import { usePageHeader } from "contexts/PageHeaderContext";



type MannatNiyazHead = 'Zabihat' | 'Mannat' | 'Niyaz';

interface HeadDetail {
  units: number;
  customAmount: number;
  discount: number;
  amount: number;
}

interface MumeenInfo {
  name: string;
  phone: string;
  email: string;
  watan: string;
}

interface MannatNiyazReceipt {
  publicId?: string;
  receiptNumber: string;
  receiptId?: string;
  receiptNo?: string;
  fullName?: string;
  currencyCode?: string;
  totalAmount?: string;
  isApproved?: boolean,
  venueName?: string;
  location: string;
  itsNo: string;
  mumeenInfo: MumeenInfo;
  selectedHeads: MannatNiyazHead[];
  headDetails: Partial<Record<MannatNiyazHead, HeadDetail>>;
  currency: string;
  total: number;
  approved: number;
  createdAt: string;
  userId: string;
  userName: string;
  currencySymbol?: string
}

export interface VenueApi {
  venueId: number;
  venueName: string;
  contactNo: string;
  emailAddress: string;
  description: string;
  timeZone: string;
  isActive: boolean;
}


const approvalStatuses = [
  { value: '0', label: 'Pending Approval' },
  { value: '1', label: 'Approved' },
  { value: 'all', label: 'All Statuses' }
];

interface MannatNiyazDashboardPageProps {
  onNavigate?: (page: string) => void;
}

interface MenuItem {
  moduleScreenId: number;
  sortOrder: number;
  screenCode: string;
  screenName: string;
  label: string;
  icon: string | null;
  iconColor: string | null;
  iconBg: string | null;
  path: string | null;
  moduleName: string | null;
  items: MenuItem[];
  categoryName: string | null;
}

interface MenuModule {
  moduleId: number;
  id: string;
  moduleName: string;
  moduleCode: string;
  label: string;
  color: string;
  items: MenuItem[];
}

interface Users {
  userId?: number | string;
  fullName?: string;
}

export function MannatNiyazDashboardPage({ onNavigate }: MannatNiyazDashboardPageProps) {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { setPageHeader } = usePageHeader();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<Users[]>([])
  const [venues, setVenues] = useState<VenueApi[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [currencyHead, setCurrencyHead] = useState<CurrencyHead[]>([]);

  // const [receipts, setReceipts] = useState<MannatNiyazReceipt[]>([]);
  const [filteredReceipts, setFilteredReceipts] = useState<MannatNiyazReceipt[]>([]);
  const [receiptTotalCounts, setReceiptsTotalCounts] = useState(0)
  const [selectedReceipts, setSelectedReceipts] = useState<string[]>([]);

  // Get today's date in YYYY-MM-DD format
  const getTodayDate = () => new Date().toISOString().split('T')[0];

  // Filters with default values
  const [filterLocation, setFilterLocation] = useState(''); // Default: KRB
  const [filterUser, setFilterUser] = useState(''); // Default: KRB
  const [filterApproved, setFilterApproved] = useState("all"); // Default: 0 (Pending)
  const [filterHead, setFilterHead] = useState('');
  const [filterCurrency, setFilterCurrency] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState(getTodayDate()); // Default: Today
  const [filterDateTo, setFilterDateTo] = useState(getTodayDate()); // Default: Today

  // User info
  // const [isAdmin, setIsAdmin] = useState(false);
  // const [currentUserId, setCurrentUserId] = useState('');
  // const [currentUserName, setCurrentUserName] = useState('');

  const storedMenus: MenuModule[] = JSON.parse(localStorage.getItem("fhims_menu") || "[]");
  const mtnzModule = storedMenus.find((module) => module.moduleCode === "MTNZ");
  const hasApprovalDashboard = mtnzModule?.items?.some((item) => item.screenCode === "MTNZ-DSHBD");
  const hasFinanceDashboard = mtnzModule?.items?.some((item) => item.screenCode === "MTNZ-DASBD");

  const { screenName, icon: ScreenIcon} = useScreenPermission();


  const [filters, setFilters] = useState({
    pageNo: 1,
    pageSize: 20,
  });

  const mergedFilters = useMemo(
    () => ({
      ...filters,
      locationCode: filterLocation || undefined,
      userId: filterUser ? filterUser : user?.id,
      isApproved: filterApproved !== "all" ? filterApproved === "1" : undefined,
      headId: filterHead ? Number(filterHead) : undefined,
      currencyCode: filterCurrency || undefined,
      // fromDate: filterDateFrom || undefined,
      // toDate: filterDateTo || undefined,
      fromDate: filterDateFrom !== getTodayDate() ? filterDateFrom : undefined,
      toDate: filterDateTo !== getTodayDate() ? filterDateTo : undefined,
    }),
    [filters,
      filterLocation,
      filterUser,
      filterApproved,
      filterHead,
      filterCurrency,
      user?.id,
      filterDateFrom,
      filterDateTo,
    ],
  );


  const resetFilters = () => {
    // reset pagination
    setFilters({
      pageNo: 1,
      pageSize: 20,
    });
    // reset filter states
    setFilterLocation("");
    setFilterUser("");
    setFilterApproved("0");
    setFilterHead("");
    setFilterCurrency("");
    setFilterDateFrom(getTodayDate());
    setFilterDateTo(getTodayDate());
  };

  const debouncedFilters = useDebounce(mergedFilters, 400);

  const fetchMiqaatNiyazList = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getMiqaatNiyazDashboardList(debouncedFilters);
      setFilteredReceipts(data.items);
      setReceiptsTotalCounts(data.totalCount);
    } catch {
      toast.error("Failed to fetch Ziyarat");
    } finally {
      setLoading(false);
    }
  }, [debouncedFilters]);


  const getMiqaatUserList = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getMiqaatUsers();
      setUsers(data.users);
    } catch {
      toast.error("Failed to fetch Ziyarat");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setPageHeader({ 
      title: screenName,
      icon: <ScreenIcon className="h-6 w-6" />,
    });

    return () => setPageHeader(null);
  }, [screenName, ScreenIcon, setPageHeader]);

  useEffect(() => {
    fetchMiqaatNiyazList();
    if (!hasFinanceDashboard) {
      getMiqaatUserList()
    }
  }, [fetchMiqaatNiyazList, getMiqaatUserList, hasFinanceDashboard]);

  // FETCH CURRENCIES AND CITY DATA ON COMPONENT MOUNT
  useEffect(() => {
    const fetchCurrencies = async () => {
      const data = await getAllCurrencies();
      setCurrencies(data);
    };
    const fetchVenues = async () => {
      const data = await getVenues();
      setVenues(data);
    };
    const fetchCurrencyHead = async () => {
      const data = await getCurrencyHead();
      setCurrencyHead(data);
    };
    fetchCurrencies();
    fetchVenues();
    fetchCurrencyHead()
  }, []);



  // Handle approval checkbox selection
  const handleSelectReceipt = (receiptNumber: string) => {
    setSelectedReceipts(prev => {
      if (prev.includes(receiptNumber)) {
        return prev.filter(r => r !== receiptNumber);
      } else {
        return [...prev, receiptNumber];
      }
    });
  };

  // Handle select all checkboxes
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const pendingReceipts = filteredReceipts
        .filter(r => r.isApproved === Boolean(0))
        .map(r => r?.receiptNo)
        .filter((receiptNo): receiptNo is string => Boolean(receiptNo));
      setSelectedReceipts(pendingReceipts);
    } else {
      setSelectedReceipts([]);
    }
  };


  const handleApprove = async () => {
    if (!hasApprovalDashboard && hasFinanceDashboard) {
      toast.error("Only admins can approve receipts");
      return;
    }

    if (selectedReceipts.length === 0) {
      toast.error("Please select at least one receipt to approve");
      return;
    }

    try {
      // get selected receipt ids
      const receiptIds = filteredReceipts.filter((r) => selectedReceipts.includes(String(r.receiptNo))).map((r) => Number(r.receiptId));
      await approveReceipts({
        receiptIds,
        approvedByUserId: Number(user?.id) || Number(localStorage.getItem("userId")) || 0,
      });

      // update UI
      const updatedReceipts = filteredReceipts.map((r) => {
        if (selectedReceipts.includes(String(r.receiptNo))) {
          return {
            ...r,
            isApproved: true,
          };
        }
        return r;
      });
      setFilteredReceipts(updatedReceipts);

      localStorage.setItem("mannatNiyazReceipts", JSON.stringify(updatedReceipts));
      toast.success(`${selectedReceipts.length} receipt(s) approved successfully!`);
      setSelectedReceipts([]);
    } catch (error: unknown) {
      console.error(error);
      if (axios.isAxiosError(error) && error.response?.data) {
        const message = error.response.data.message || "Failed to approve receipts";
        toast.error(message);
      }
    }
  };

  // Handle export to CSV
  const handleExportCSV = () => {
    if (filteredReceipts.length === 0) {
      toast.error('No receipts to export');
      return;
    }

    const headers = ['Receipt Number', 'Location', 'User', 'Date', 'Mumeen Name', 'ITS No', 'Heads', 'Currency', 'Total', 'Status'];
    const csvData = filteredReceipts.map(r => [
      r.receiptNo,
      r.venueName,
      r.userName,
      new Date(r.createdAt).toLocaleDateString(),
      r?.fullName || 'N/A',
      r.itsNo,
      Array.isArray(r.headDetails)
        ? r.headDetails.map((head) => head.headName).join(", ")
        : "N/A",
      r.currencyCode,
      `${r.currencySymbol || ""}${r.totalAmount}`,
      r.isApproved === Boolean(1) ? 'Approved' : 'Pending'
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mannat-niyaz-receipts-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);

    toast.success('CSV file downloaded successfully!');
  };

  // Summary 1: Currency-wise totals
  const getCurrencySummary = () => {
    const summary: { [key: string]: number } = {};

    filteredReceipts.forEach(receipt => {
      if (!summary[String(receipt.currencyCode)]) {
        summary[String(receipt.currencyCode)] = 0;
      }
      summary[String(receipt?.currencyCode)] += Number(receipt?.totalAmount);
    });

    return summary;
  };

  // Summary 2: Head-wise transaction count
  const getHeadSummary = () => {
    const summary: { [key: string]: number } = {};

    filteredReceipts.forEach(receipt => {
      if (receipt.headDetails && Array.isArray(receipt.headDetails)) {
        receipt.headDetails.forEach((head) => {
          if (!summary[head.headName]) {
            summary[head.headName] = 0;
          }
          summary[head.headName] += 1;
        });
      }
    });

    return summary;
  };

  const currencySummary = getCurrencySummary();
  const headSummary = getHeadSummary();

  const getCurrencySymbol = (currencyCode: string) => {
    return currencies.find(c => c.value === currencyCode)?.currencySymbol || currencyCode;
  };


  // const handleViewReceipt = async (receipt: MannatNiyazReceipt, download = false) => {
  //   try {
  //     setLoading(true);
  //     const response = await getReceiptPdf(String(receipt.publicId), download);
  //     const blob = new Blob([response.data], { type: "application/pdf" });
  //     const blobUrl = window.URL.createObjectURL(blob);

  //     // DOWNLOAD PDF
  //     if (download) {
  //       const link = document.createElement("a");
  //       link.href = blobUrl;
  //       link.download = `${receipt.receiptNo}.pdf`;
  //       link.click();
  //       return;
  //     }

  //     // OPEN PDF IN NEW TAB
  //     window.open(blobUrl, "_blank");

  //     // window.open(`http://fhims.najafkarbala.com:5153/api/Receipts/${receipt.publicId}/pdf?download=false`,
  //     // window.open(`${process.env.REACT_APP_MANNAT_NIYAZ_RECEIPT_BASE_URL}/${receipt.publicId}/pdf?download=false`,
  //     //   "_blank"
  //     // );
  //   } catch (error: unknown) {
  //     if (axios.isAxiosError(error) && error.response?.data) {
  //       const message = error.response.data.message || "Failed to load receipt PDF";
  //       toast.error(message);
  //     }
  //   } finally {
  //     setLoading(false)
  //   }
  // };

  const handleViewReceipt = async (receipt: MannatNiyazReceipt, download = false) => {
    try {
      setLoading(true);
      const response = await getReceiptPdf(String(receipt.publicId), download);
      const baseUrl = `${process.env.REACT_APP_MANNAT_NIYAZ_RECEIPT_BASE_URL}/${receipt.publicId}/pdf?download=${download}`;
       if (response.status === 200) {

         // DOWNLOAD PDF
         if (download) {
           const url = baseUrl;
           window.open(url, "_blank");
         }

         // OPEN PDF IN NEW TAB
         const url = baseUrl;
         window.open(url, "_blank");
       }
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.response?.data) {
        const message = error.response.data.message || "Failed to load receipt PDF";
        toast.error(message);
      }
    } finally {
      setLoading(false)
    }
  };


  const handleSendEmail = async (receipt: MannatNiyazReceipt) => {
    try {
      setLoading(true)
      // const response = await SendReceiptEmail(String(receipt.publicId));
      await SendReceiptEmail(String(receipt.publicId));
      toast.success(
        "Receipt email sent successfully"
      );
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.response?.data) {
        const message = error.response.data.message || "Failed to send receipt email";
        toast.error(message);
      }
    } finally {
      setLoading(false)
    }
  };


  return (
    <div className="min-h-screen pb-20">
      {loading && <FullScreenLoading />}
      {/* <PageHeader
        title="Mannat & Niyaz Dashboard"
        description="View and manage Zabihat, Mannat, and Niyaz receipts"
        icon={<BarChart3 className="w-6 h-6" />}
      /> */}

      <div className="mx-auto space-y-6">
        {/* Quick Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <Card className="p-5 bg-gradient-to-br from-sky-50 to-sky-100 border-sky-300" style={{ marginTop: "0px" }}>
            <div className="text-sm text-sky-700 mb-1">Total Receipts</div>
            <div className="text-3xl text-sky-900">{filteredReceipts.length}</div>
          </Card>
          <Card className="p-5 bg-gradient-to-br from-green-50 to-green-100 border-green-300" style={{ marginTop: "0px" }}>
            <div className="text-sm text-green-700 mb-1">Approved</div>
            <div className="text-3xl text-green-900">{filteredReceipts.filter(r => r.isApproved === Boolean(1)).length}</div>
          </Card>
          <Card className="p-5 bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-300" style={{ marginTop: "0px" }}>
            <div className="text-sm text-yellow-700 mb-1">Pending Approval</div>
            <div className="text-3xl text-yellow-900">{filteredReceipts.filter(r => r.isApproved === Boolean(0)).length}</div>
          </Card>
          <Card className="p-5 bg-gradient-to-br from-purple-50 to-purple-100 border-purple-300" style={{ marginTop: "0px" }}>
            <div className="text-sm text-purple-700 mb-1">Currencies Used</div>
            <div className="text-3xl text-purple-900">{Object.keys(currencySummary).length}</div>
          </Card>
        </div>

        {/* Filters Section */}
        <Card className="p-6 bg-white/80 backdrop-blur-sm border-sky-200">
          <div className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-sky-600" />
            <span className="font-medium text-sky-900">Filters</span>
            <span className="text-xs text-gray-500 ml-2">(Results auto-update with default values)</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Location Filter - Default: KRB */}
            {!hasFinanceDashboard && hasApprovalDashboard && <div>
              <Label htmlFor="filterLocation" className="text-sky-900">Location</Label>
              <Select value={filterLocation || ""} onValueChange={setFilterLocation}>
                <SelectTrigger id="filterLocation" className="mt-1">
                  <SelectValue placeholder="Select Location" />
                </SelectTrigger>
                <SelectContent>
                  {venues?.map(loc => (
                    <SelectItem key={loc.venueId} value={String(loc.venueId)}>
                      {loc.venueName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>}


            {/* Approval Status Filter - Default: 0 (Pending) */}
            <div>
              <Label htmlFor="filterApproved" className="text-sky-900">Approval Status</Label>
              <Select value={filterApproved} onValueChange={setFilterApproved}>
                <SelectTrigger id="filterApproved" className="mt-1">
                  <SelectValue placeholder="Select Approval Status" />
                </SelectTrigger>
                <SelectContent>
                  {approvalStatuses.map(status => (
                    <SelectItem key={status.value} value={status.value}>
                      {status.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* User Filter - Read-only if NOT admin */}

            {!hasFinanceDashboard && hasApprovalDashboard && <div>
              <Label htmlFor="filterUser" className="text-sky-900">User</Label>
              <Select value={filterUser || ""} onValueChange={setFilterUser}>
                <SelectTrigger id="filterUser" className="mt-1">
                  <SelectValue placeholder="Select User" />
                </SelectTrigger>
                <SelectContent>
                  {users?.map((user, i) => (
                    <SelectItem key={user.userId || i} value={String(user.userId)}>
                      {user.fullName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>}

            {/* Head Filter */}
            <div>
              <Label htmlFor="filterHead" className="text-sky-900">Head</Label>
              <Select value={filterHead} onValueChange={setFilterHead}>
                <SelectTrigger id="filterHead" className="mt-1">
                  <SelectValue placeholder="Select Head" />
                </SelectTrigger>
                <SelectContent>
                  {currencyHead?.map(h => (
                    <SelectItem key={h.currencyHeadMasterId} value={String(h.currencyHeadMasterId)}>
                      {h.headName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Currency Filter */}
            <div>
              <Label htmlFor="filterCurrency" className="text-sky-900">Currency</Label>
              <Select value={filterCurrency} onValueChange={setFilterCurrency}>
                <SelectTrigger id="filterCurrency" className="mt-1">
                  <SelectValue placeholder="Select Currency" />
                </SelectTrigger>
                <SelectContent>
                  {currencies.map(curr => (
                    <SelectItem key={curr.currencyCode} value={curr.currencyCode}>
                      {curr.currencyCode} ({curr.currencySymbol})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date From Filter - Default: Today */}
            <div>
              <Label htmlFor="filterDateFrom" className="text-sky-900">Date From</Label>
              <Input
                id="filterDateFrom"
                type="date"
                value={filterDateFrom}
                onChange={(e) => setFilterDateFrom(e.target.value)}
                className="mt-1"
              />
            </div>

            {/* Date To Filter - Default: Today */}
            <div>
              <Label htmlFor="filterDateTo" className="text-sky-900">Date To</Label>
              <Input
                id="filterDateTo"
                type="date"
                value={filterDateTo}
                onChange={(e) => setFilterDateTo(e.target.value)}
                className="mt-1"
              />
            </div>

            <div className='mt-3'>
              <Button className="w-full bg-red-300 mt-1" onClick={resetFilters}>Reset</Button>
            </div>
          </div>
        </Card>

        {/* Results Table */}
        <Card className="p-6 bg-white/80 backdrop-blur-sm border-sky-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-sky-600" />
              <span className="font-medium text-sky-900">
                Receipt Results ({filteredReceipts.length || receiptTotalCounts})
              </span>
            </div>

            <div className="flex flex-wrap justify-end lg:text-sm gap-2">
              <Button
                variant="outline"
                onClick={handleExportCSV}
                disabled={filteredReceipts.length === 0}
              >
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/receipts')}
              >
                <Plus className="h-4 w-4 mr-2" />
                New Receipt
              </Button>
              {!hasFinanceDashboard && hasApprovalDashboard && (
                <Button
                  onClick={handleApprove}
                  disabled={selectedReceipts.length === 0}
                  className="min-w-[180px]"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Approve Selected ({selectedReceipts.length})
                </Button>
              )}
            </div>
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-sky-50">
                  {!hasFinanceDashboard && hasApprovalDashboard && (
                    <TableHead className="w-12">
                      <Checkbox
                        checked={selectedReceipts.length > 0 && selectedReceipts.length === filteredReceipts.filter(r => r.isApproved === Boolean(0)).length}
                        onCheckedChange={handleSelectAll}
                      />
                    </TableHead>
                  )}
                  <TableHead>Receipt #</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Mumeen</TableHead>
                  <TableHead>Heads</TableHead>
                  <TableHead>Curr</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredReceipts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={!hasFinanceDashboard && hasApprovalDashboard ? 9 : 8} className="text-center py-12 text-gray-500">
                      <FileText className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                      <p>No receipts found matching the filters</p>
                      <p className="text-sm mt-1">Try adjusting the filter criteria or create a new receipt</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredReceipts.map(receipt => (
                    <TableRow
                      key={receipt.receiptNo}
                      className="hover:bg-sky-50/50 cursor-pointer"
                      onClick={() => handleViewReceipt(receipt, false)}
                    >
                      {!hasFinanceDashboard && hasApprovalDashboard && (
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedReceipts.includes(String(receipt?.receiptNo))}
                            onCheckedChange={() => handleSelectReceipt(String(receipt?.receiptNo))}
                            disabled={receipt.isApproved === Boolean(1)}
                          />
                        </TableCell>
                      )}
                      <TableCell className="font-mono text-sm">{receipt.receiptNo}</TableCell>
                      <TableCell>{receipt.venueName}</TableCell>
                      <TableCell>{receipt.userName}</TableCell>
                      <TableCell>{new Date(receipt.createdAt).toLocaleDateString()}</TableCell>
                      <TableCell>{receipt.mumeenInfo?.name || receipt?.fullName}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {receipt.headDetails && Array.isArray(receipt.headDetails) ? (
                            receipt.headDetails.map((head, i) => (
                              <span key={i} className={`inline-flex px-2 py-1 rounded text-xs ${head.headName === 'Niyaz - Food' ? 'bg-red-100 text-red-800' :
                                head.headName === 'Mannat - General' ? 'bg-blue-100 text-blue-800' :
                                  'bg-green-100 text-green-800'
                                }`}>
                                {head.headName}
                              </span>
                            ))
                          ) : (
                            <span>N/A</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{receipt.currencyCode}</TableCell>
                      <TableCell>
                        {receipt?.currencySymbol}{receipt?.totalAmount?.toLocaleString() || ""}
                      </TableCell>
                      <TableCell>
                        {receipt?.isApproved === Boolean(1) ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                            <CheckCircle className="h-3 w-3" />
                            Approved
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800">
                            Pending
                          </span>
                        )}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-100"
                            onClick={() => handleViewReceipt(receipt, false)}
                            title="View Receipt"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-sky-600 hover:text-sky-700 hover:bg-sky-100"
                            onClick={() => handleViewReceipt(receipt, true)}
                            title="Download Slip"
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-green-600 hover:text-green-700 hover:bg-green-100"
                            onClick={() =>
                              handleSendEmail(receipt)
                            }
                            title="Send Email"
                          >
                            <MailCheck className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* PAGINATION COMPONENT FOR DRAFT */}
          {/* <Pagination
            totalItems={20}
            itemsPerPage={filters.pageSize}
            currentPage={filters.pageNo}
            onPageChange={(page) =>
              setFilters((prev) => ({
                ...prev,
                pageNo: page,
              }))
            }
            onPageSizeChange={(size) =>
              setFilters((prev) => ({
                ...prev,
                pageSize: size,
                pageNo: 1,
              }))
            }
            label="Receipts Pagination"
          /> */}
        </Card>

        {/* Summary Sections */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Summary 1: Currency-wise Collection */}
          <Card className="p-6 bg-gradient-to-br from-sky-50 to-blue-50 border-sky-300">
            <div className="mb-4 flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-sky-600" />
              <span className="font-medium text-sky-900">Summary 1: Currency-wise Collection</span>
            </div>
            <div className="space-y-3">
              {Object.keys(currencySummary).length === 0 ? (
                <p className="text-gray-500 text-center py-6">No collection data available</p>
              ) : (
                Object.entries(currencySummary)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([currency, total]) => (
                    <div key={currency} className="flex justify-between items-center p-4 bg-white rounded-lg border border-sky-200 shadow-sm">
                      <span className="text-sky-900">{currency}</span>
                      <span className="text-sky-900">
                        {getCurrencySymbol(currency)} {total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  ))
              )}
            </div>
          </Card>

          {/* Summary 2: Head-wise Transaction Count */}
          <Card className="p-6 bg-gradient-to-br from-green-50 to-emerald-50 border-green-300">
            <div className="mb-4 flex items-center gap-2">
              <FileText className="h-5 w-5 text-green-600" />
              <span className="font-medium text-green-900">Summary 2: Head-wise Transactions</span>
            </div>
            <div className="space-y-3">
              {Object.keys(headSummary).length === 0 ? (
                <p className="text-gray-500 text-center py-6">No transaction data available</p>
              ) : (
                Object.entries(headSummary)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([head, count]) => (
                    <div key={head} className="flex justify-between items-center p-4 bg-white rounded-lg border border-green-200 shadow-sm">
                      <span className="text-green-900">{head}</span>
                      <span className="text-green-900">
                        {count} transaction{count !== 1 ? 's' : ''}
                      </span>
                    </div>
                  ))
              )}
            </div>
          </Card>
        </div>
      </div >
    </div >
  );
}